import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress, MenuItem } from '@mui/material'
import { useIntl } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import LoanLockedAlert from './LoanLockedAlert'
import LoanSummary from './LoanSummary'
import { useLoanContext } from '../../contexts/LoanContext'
import useUser from '../../hooks/useUser'
import { navigationLinking } from '../../services/navigation'
import ActionMenu from '../ActionMenu'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import useErrorHandler from '../../hooks/useErrorHandler'
import { useAlert } from '../../hooks'

const LoanHeader = () => {
	const { loan, loanData, refetch } = useLoanContext()
	const { isLoanAdmin } = useUser()
	const navigate = useNavigate()
	const { formatMessage } = useIntl()
	const { handleError } = useErrorHandler()
	const { alert } = useAlert()

	const [isActionMenuOpen, setIsActionMenuOpen] =
		React.useState(false)

	const handleActionMenuOpen = () => {
		setIsActionMenuOpen(true)
	}

	const handleActionMenuClose = () => {
		setIsActionMenuOpen(false)
	}

	const isPosLoan = loanData?.IsPOSLoan ?? false

	const handleLoanSettingsClick = () => {
		navigate(`/${navigationLinking.LoanSettings}`)
	}

	const importFromEncompassMutation = useMutation({
		mutationFn: () => {
			if (!loan?.loanID) {
				return Promise.reject(
					new Error(
						formatMessage({
							id: 'loans.los_id_is_required_to_perform_operation',
						})
					)
				)
			}
			return TheBigPOSApi.importLoanFromLos(loan.loanID)
		},
	})

	const handleSyncWithLosClick = async () => {
		try {
			await importFromEncompassMutation.mutateAsync()
			if (refetch) {
				await refetch()
			}
			alert(formatMessage({ id: 'loans.loan_synced_with_los' }), {
				severity: 'success',
			})
		} catch (e) {
			const errorResponse = handleError(e)
			alert(errorResponse.message, { severity: 'error' })
		} finally {
			handleActionMenuClose()
		}
	}

	return (
		<div className="border-b px-4 pt-3 pb-2">
			<div className="flex flex-row items-center justify-between">
				<div className="flex flex-row items-center">
					<LoanLockedAlert className="flex flex-row items-center mr-2" />
					<LoanSummary />
				</div>
				{isLoanAdmin && isPosLoan && (
					<ActionMenu
						open={isActionMenuOpen}
						onOpen={handleActionMenuOpen}
						onClose={handleActionMenuClose}
					>
						<MenuItem onClick={handleLoanSettingsClick}>
							{formatMessage({ id: 'loan_settings.heading' })}
						</MenuItem>
						{loan?.loanID && (
							<MenuItem
								onClick={handleSyncWithLosClick}
								disabled={importFromEncompassMutation.isPending}
							>
								{formatMessage({ id: 'loans.sync_with_los' })}
								{importFromEncompassMutation.isPending && (
									<CircularProgress size={12} className="ml-2" />
								)}
							</MenuItem>
						)}
					</ActionMenu>
				)}
			</div>
		</div>
	)
}

export default LoanHeader
